// Route: /tools/visual-explainer/[id] — детальный разбор изображения.
// Desktop split: панель просмотра (зум) | скроллящееся структурное объяснение.
// Mobile: вертикальный стек (изображение → объяснение).
// URL state: ?demo=loading|error|processing|graded
//
// Phase 4 — реальный tRPC через useVisualDetail (poll до ready/failed) +
// Socket.IO invalidate на `visual:ready`/`visual:status_changed` для мгновенной
// реакции (поллинг — fallback).
//
// Редизайн (visual-audio-tutor): встроенный follow-up чат убран. Уточнения
// делегированы глобальному AI-репетитору (правая боковая панель,
// useSidePanelStore) — контекст изображения доклеивается автоматически хуком
// usePageContext по роуту. В шапке — кнопка «Спросить репетитора»; снизу —
// AskTutorBar с быстрыми чипами и ИИ-дисклеймером.

import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import {
	Button,
	cn,
	EmptyState,
	ErrorState,
	FIT_APP_PAGE_SHELL,
	LIST_PAGE_SHELL,
	type LoaderStep,
	MagicMomentLoader,
	NARROW_PAGE_SHELL,
	Skeleton,
	STATUS_PAGE_SHELL,
} from "@studyhub/ui";
import { ScrollArea } from "@studyhub/ui/components/scroll-area";
import { IconAlertTriangle, IconArrowLeft, IconRefresh } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { OfflineDetailState } from "@/components/shared/offline-detail-state";
import { AskTutorHeaderButton } from "@/components/tutor/ask-tutor-header-button";
import { useRetryVisual, useVisualDetail } from "@/components/visual-explainer/_api";
import { AskTutorBar } from "@/components/visual-explainer/ask-tutor-bar";
import { CopyToNoteButton } from "@/components/visual-explainer/copy-to-note-button";
import { ExplanationViewer } from "@/components/visual-explainer/explanation-viewer";
import { ZoomableImage } from "@/components/visual-explainer/zoomable-image";
import { useArtifactWatching } from "@/hooks/use-artifact-watching";
import { markArtifactSeenKey, useMarkArtifactSeen } from "@/hooks/use-mark-artifact-seen";
import { useRequireOnline } from "@/hooks/use-require-online";
import { useSocketGenerationProgress } from "@/hooks/use-socket-generation-progress";
import { useTrackView } from "@/hooks/use-track-view";
import { isOfflineWithoutCache } from "@/lib/offline-query-state";
import { useIsLgUp } from "@/lib/platform-detect";
import { useSocket } from "@/providers/socket-provider";

const searchSchema = z.object({
	demo: z.enum(["default", "loading", "error", "processing"]).optional(),
});

export const Route = createFileRoute("/tools/visual-explainer/$id")({
	validateSearch: tolerantSearch(searchSchema),
	component: VisualDetailPage,
	pendingComponent: VisualDetailSkeleton,
});

function VisualDetailPage(): React.ReactElement {
	const { t } = useTranslation("visual-explainer");
	const { online, guard } = useRequireOnline();
	const { id } = useParams({ from: "/tools/visual-explainer/$id" });
	useTrackView("visual", id);
	useArtifactWatching(markArtifactSeenKey("visual", id));
	const search = Route.useSearch();
	const isDesktop = useIsLgUp();
	const detailQuery = useVisualDetail(id);
	const retry = useRetryVisual();
	const queryClient = useQueryClient();
	const socket = useSocket();
	const markSeen = useMarkArtifactSeen();

	// Мгновенная реакция на завершение анализа: Inngest эмитит
	// `visual:ready` / `visual:status_changed` в room `user:${userId}`.
	// Инвалидация → getById перезапрашивается сразу (не ждём poll-тик).
	// Поллинг в useVisualDetail остаётся надёжным fallback'ом.
	useEffect(() => {
		if (!socket || !id) return;
		const invalidate = (payload: { queryId: string }): void => {
			if (payload.queryId !== id) return;
			void queryClient.invalidateQueries({
				queryKey: [["visualExplainer", "analyses", "getById"], { input: { queryId: id } }],
			});
		};
		// Surface-ack: разбор этого id завершился (status==='ready') и пользователь
		// на его странице (видим) → гасим email/push + колокол (presence-aware
		// §3.3, матрица 4.2). scope по queryId === route id.
		const onStatusChanged = (payload: { queryId: string; status: string }): void => {
			invalidate(payload);
			if (payload.queryId === id && payload.status === "ready") {
				markSeen(markArtifactSeenKey("visual", id), "surface");
			}
		};
		socket.on("visual:ready", invalidate);
		socket.on("visual:status_changed", onStatusChanged);
		return () => {
			socket.off("visual:ready", invalidate);
			socket.off("visual:status_changed", onStatusChanged);
		};
	}, [socket, id, queryClient, markSeen]);
	const isLoading = search.demo === "loading" || detailQuery.isLoading;
	const isError = search.demo === "error" || detailQuery.isError;
	const isProcessing = search.demo === "processing";
	const data = detailQuery.data;

	if (isLoading) return <VisualDetailSkeleton />;
	if (isError) {
		return (
			<div className={STATUS_PAGE_SHELL}>
				<ErrorState
					title={t("detail.errorTitle")}
					description={t("list.errorSubtitle")}
					action={
						<Button asChild variant="outline">
							<Link to="/tools/visual-explainer">
								<IconArrowLeft size={16} />
								{t("detail.back")}
							</Link>
						</Button>
					}
				/>
			</div>
		);
	}
	// Офлайн без локальной копии: paused-запрос не даст ни isLoading, ни isError —
	// раньше это оборачивалось вечным скелетоном.
	if (isOfflineWithoutCache(detailQuery)) {
		return (
			<div className={STATUS_PAGE_SHELL}>
				<OfflineDetailState onRetry={() => void detailQuery.refetch()} />
			</div>
		);
	}
	if (!data) return <VisualDetailSkeleton />;

	const { query, explanation, imageUrl } = data;
	const status = query.status;

	if (isProcessing || status === "analyzing" || status === "queued") {
		return <VisualAnalyzingState queryId={query.id} status={isProcessing ? "analyzing" : status} />;
	}
	if (status === "failed") {
		return (
			<div className={STATUS_PAGE_SHELL}>
				<EmptyState
					icon={<IconAlertTriangle />}
					title={t("detail.failedTitle")}
					description={t("detail.failedSubtitle")}
					action={
						<div className="flex items-center gap-2">
							<Button
								onClick={() => {
									if (!guard()) return;
									retry.mutate({ queryId: query.id });
								}}
								disabled={retry.isPending || !online}
							>
								<IconRefresh size={16} />
								{retry.isPending ? t("detail.retrying") : t("detail.retry")}
							</Button>
							<Button asChild variant="outline">
								<Link to="/tools/visual-explainer">
									<IconArrowLeft size={16} />
									{t("detail.back")}
								</Link>
							</Button>
						</div>
					}
				/>
			</div>
		);
	}
	if (!explanation) {
		// status='ready' но explanation отсутствует — теоретически invariant violation.
		return <VisualDetailSkeleton />;
	}

	const explanationBlock = (
		<ExplanationViewer
			title={explanation.title}
			category={explanation.category}
			explanation={explanation.explanation}
			keyPoints={explanation.keyPoints}
		/>
	);

	// Desktop: изображение слева (как есть, с зумом) | скроллящийся конспект
	// объяснения справа + фиксированная нижняя полоса AskTutorBar.
	if (isDesktop) {
		return (
			<div className={FIT_APP_PAGE_SHELL}>
				{/* Тулбар: назад + действия (конспект, репетитор). */}
				<div className="flex shrink-0 items-center justify-between gap-3">
					<Button
						asChild
						variant="ghost"
						size="sm"
						className="-ml-2 h-8 w-fit px-2 text-muted-foreground"
					>
						<Link to="/tools/visual-explainer">
							<IconArrowLeft size={14} />
							{t("detail.back")}
						</Link>
					</Button>
					<div className="flex shrink-0 items-center gap-2">
						<CopyToNoteButton visualAnalysisId={query.id} />
						<AskTutorHeaderButton ns="visual" labelKey="askTutor.cta" />
					</div>
				</div>

				<div className="grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] gap-4">
					<ZoomableImage src={imageUrl} alt={explanation.title} className="h-full" />
					{/* Чистый конспект объяснения: что ассистент видит на изображении +
					    как решается задача. БЕЗ чат-бабблов и композера. */}
					<div className="flex min-h-0 flex-col overflow-hidden rounded-[var(--radius-panel)] border border-border bg-card shadow-e2">
						<ScrollArea className="min-h-0 flex-1">
							<div className="p-5 lg:p-6">{explanationBlock}</div>
						</ScrollArea>
						<div className="shrink-0 border-t border-border bg-card/80 px-5 py-4 backdrop-blur lg:px-6">
							<AskTutorBar title={explanation.title} />
						</div>
					</div>
				</div>
			</div>
		);
	}

	// Mobile: вертикальный стек — изображение сверху, объяснение снизу, под ним
	// полоса действий с репетитором.
	return (
		<div className={cn(NARROW_PAGE_SHELL, "space-y-section")}>
			<div className="flex items-center justify-between gap-3">
				<Button
					asChild
					variant="ghost"
					size="sm"
					className="-ml-2 h-8 w-fit px-2 text-muted-foreground"
				>
					<Link to="/tools/visual-explainer">
						<IconArrowLeft size={14} />
						{t("detail.back")}
					</Link>
				</Button>
				<div className="flex shrink-0 items-center gap-2">
					<CopyToNoteButton visualAnalysisId={query.id} />
					<AskTutorHeaderButton ns="visual" labelKey="askTutor.cta" />
				</div>
			</div>

			<ZoomableImage src={imageUrl} alt={explanation.title} className="aspect-[4/3] w-full" />

			<div className="rounded-[var(--radius-panel)] border border-border bg-card p-4 shadow-e2">
				{explanationBlock}
			</div>

			<div className="rounded-[var(--radius-panel)] border border-border bg-card p-4 shadow-e1">
				<AskTutorBar title={explanation.title} />
			</div>
		</div>
	);
}

function VisualAnalyzingState({
	queryId,
	status,
}: {
	queryId: string;
	status: "queued" | "analyzing" | string;
}): React.ReactElement {
	const { t } = useTranslation("visual-explainer");
	const { t: tc } = useTranslation("common");
	const live = useSocketGenerationProgress({
		event: "visual:generation_progress",
		enabled: true,
		defaultCrawlMs: status === "queued" ? 20_000 : 55_000,
		match: (payload) => payload.queryId === queryId,
	});
	const progress = Math.max(live.progress, status === "queued" ? 0.08 : 0.15);
	const percent = Math.round(progress * 100);

	const ocrDone = progress >= 0.35;
	const catDone = progress >= 0.55;
	const steps: LoaderStep[] = [
		{
			label: t("detail.analyzing.steps.ocr"),
			status: ocrDone ? "done" : "in-progress",
		},
		{
			label: t("detail.analyzing.steps.categorize"),
			status: catDone ? "done" : ocrDone ? "in-progress" : "pending",
		},
		{
			label: t("detail.analyzing.steps.explain"),
			status: progress >= 0.9 ? "done" : catDone ? "in-progress" : "pending",
		},
	];

	return (
		<div className={STATUS_PAGE_SHELL}>
			<Button
				asChild
				variant="ghost"
				size="sm"
				className="-ml-2 mb-4 h-8 w-fit px-2 text-muted-foreground"
			>
				<Link to="/tools/visual-explainer">
					<IconArrowLeft size={14} />
					{t("detail.back")}
				</Link>
			</Button>
			<MagicMomentLoader
				title={t("detail.analyzing.title")}
				steps={steps}
				tip={t("detail.analyzing.subtitle")}
				progress={progress}
				progressLabel={tc("generation.progress", { percent, defaultValue: `${percent}%` })}
				etaLabel={live.etaLabel ?? undefined}
			/>
		</div>
	);
}

function VisualDetailSkeleton(): React.ReactElement {
	return (
		<div className={LIST_PAGE_SHELL}>
			<div className="flex items-center justify-between">
				<Skeleton className="h-8 w-24" />
				<Skeleton className="h-8 w-44" />
			</div>
			<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
				<Skeleton className="aspect-[4/3] rounded-[var(--radius-panel)] lg:aspect-auto lg:h-[560px]" />
				<div className="space-y-3 rounded-[var(--radius-panel)] border border-border bg-card p-6 shadow-e2">
					<Skeleton className="h-6 w-2/3" />
					<Skeleton className="h-24 rounded-[var(--radius-card)]" />
					<Skeleton className="h-24 rounded-[var(--radius-card)]" />
					<Skeleton className="h-32 rounded-[var(--radius-card)]" />
				</div>
			</div>
		</div>
	);
}
